import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './common.js';

export const ReceivableViewSchema = z.object({
  id: z.string().uuid(),
  quoteId: z.string().uuid().nullable(),
  descricao: z.string(),
  valor: MoneyStringSchema,
  valorRecebido: MoneyStringSchema,
  vencimento: z.string().nullable(),
  status: z.enum(['pendente', 'recebido']),
  moeda: CurrencySchema,
});
export type ReceivableViewDTO = z.infer<typeof ReceivableViewSchema>;

export const PayableViewSchema = z.object({
  id: z.string().uuid(),
  origem: z.enum(['quote', 'viagem', 'comissao', 'manual']),
  descricao: z.string(),
  valor: MoneyStringSchema,
  vencimento: z.string().nullable(),
  status: z.enum(['pendente', 'pago', 'aguarda_nota_fiscal']),
  requerNotaFiscal: z.boolean(),
  representanteId: z.string().uuid().nullable(),
  moeda: CurrencySchema,
});
export type PayableViewDTO = z.infer<typeof PayableViewSchema>;

export const CreatePayableSchema = z.object({
  descricao: z.string().min(1),
  valor: MoneyStringSchema,
  vencimento: z.string().datetime().optional(),
  representanteId: z.string().uuid().optional(),
});
export type CreatePayableDTO = z.infer<typeof CreatePayableSchema>;

export const AlertasSchema = z.object({
  recebiveisEmAtraso: z.array(ReceivableViewSchema),
  pagaveisProximos: z.array(PayableViewSchema),
});
export type AlertasDTO = z.infer<typeof AlertasSchema>;

export const DashboardSchema = z.object({
  totalReceberPendente: MoneyStringSchema,
  totalPagarPendente: MoneyStringSchema,
  totalEfetivado: MoneyStringSchema,
  pipeline: z.record(z.string(), z.number()),
});
export type DashboardDTO = z.infer<typeof DashboardSchema>;

export const ComissoesSchema = z.object({
  porRepresentante: z.array(
    z.object({
      representanteId: z.string().nullable(),
      total: MoneyStringSchema,
      quantidade: z.number(),
    }),
  ),
  total: MoneyStringSchema,
});
export type ComissoesDTO = z.infer<typeof ComissoesSchema>;

export const DescontosSchema = z.object({
  total: MoneyStringSchema,
  quantidade: z.number(),
});
export type DescontosDTO = z.infer<typeof DescontosSchema>;

/** Gráfico XY: custo (executado, linear) × receita (efetivado, "em onda") por mês — RF-FLU-03. */
export const GraficoSchema = z.object({
  pontos: z.array(
    z.object({
      mes: z.string(), // YYYY-MM
      receita: MoneyStringSchema,
      custo: MoneyStringSchema,
    }),
  ),
});
export type GraficoDTO = z.infer<typeof GraficoSchema>;

/** Fluxo projetado (RF-FLU-09): realizado (efetivado) + a receber/pagar pendentes. */
export const FluxoProjetadoSchema = z.object({
  efetivadoRealizado: MoneyStringSchema,
  aReceber: MoneyStringSchema,
  aPagar: MoneyStringSchema,
  saldoProjetado: MoneyStringSchema,
});
export type FluxoProjetadoDTO = z.infer<typeof FluxoProjetadoSchema>;

export const LancarImpostoSchema = z.object({
  valor: MoneyStringSchema,
  descricao: z.string().min(1),
});
export type LancarImpostoDTO = z.infer<typeof LancarImpostoSchema>;

/** Fechamento anual (RF-FIN-14): efetivado × executado do ano (executado travado no ano de execução). */
export const FechamentoAnualSchema = z.object({
  ano: z.number().int(),
  efetivado: MoneyStringSchema,
  executado: MoneyStringSchema,
  margem: MoneyStringSchema,
});
export type FechamentoAnualDTO = z.infer<typeof FechamentoAnualSchema>;
